// @ts-nocheck - untyped
import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import TabPageHeader from '../Common/TabPageHeader';
import SectionTitle from '../Common/SectionTitle';
import HealthPanel from '../HealthPanel';
import SummaryCards from '../SummaryCards';
import MetricsGridLayout from '../MetricsGridLayout';
import MetricsCharts from '../MetricsCharts';
import ComingSoonSection from './ComingSoonSection';
import ApiAnalytics from '../../ApiAnalytics/ApiAnalytics';

const styles = {
  serviceBlock: {
    marginBottom: '24px',
    padding: '16px 24px 24px',
    backgroundColor: 'var(--t-surface)',
    border: '1px solid var(--t-border, rgba(0,0,0,0.12))',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  toolbar: {
    marginBottom: '24px',
    padding: '20px 24px',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '20px',
    background: 'var(--t-surface)',
    border: '1px solid var(--t-border, rgba(94, 51, 191, 0.18))',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(94, 51, 191, 0.08), 0 1px 3px rgba(0,0,0,0.04)',
  },
  toolbarControl: {
    minWidth: 140,
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: 'var(--t-surface)',
      minHeight: 40,
      '& fieldset': { borderColor: 'var(--t-border, rgba(94, 51, 191, 0.25))' },
      '&:hover fieldset': { borderColor: 'var(--color-primary)' },
      '&.Mui-focused fieldset': { borderColor: 'var(--color-primary)' },
    },
  },
  lastUpdated: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  timeRangeBtn: {
    minWidth: 200,
    minHeight: 40,
    borderRadius: '12px',
    backgroundColor: 'var(--t-surface)',
    borderColor: 'var(--t-border, rgba(94, 51, 191, 0.25))',
    color: 'var(--t-fg, rgba(0, 0, 0, 0.87))',
    textTransform: 'none',
    '& .MuiButton-startIcon': {
      color: 'var(--color-primary)',
    },
    '&:hover': {
      borderColor: 'var(--color-primary)',
      backgroundColor: 'rgba(94, 51, 191, 0.04)',
    },
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    gap: '20px',
    padding: '24px',
  },
  refreshOverlay: {
    position: 'relative',
    transition: 'opacity 0.25s ease-out',
  },
  refreshOverlayInner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '4px',
    zIndex: 1,
  },
  errorBox: {
    padding: '28px',
    textAlign: 'center',
    borderRadius: '4px',
    border: '1px solid rgba(211, 47, 47, 0.5)',
    backgroundColor: 'var(--t-surface)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
  endpointSection: {
    marginTop: '28px',
    padding: '24px 28px',
    border: '1px solid rgba(94, 51, 191, 0.18)',
    borderRadius: '16px',
    boxShadow: '0 4px 16px rgba(94, 51, 191, 0.08), 0 1px 3px rgba(0,0,0,0.04)',
  },
  endpointSectionTitle: {
    color: 'var(--t-accent-text)',
    fontWeight: 700,
    fontSize: '1.1rem',
    letterSpacing: '0.02em',
    marginBottom: '6px',
  },
  endpointSectionDesc: {
    color: 'text.secondary',
    fontSize: '0.95rem',
    marginBottom: '16px',
    lineHeight: 1.5,
  },
  endpointSelect: {
    minWidth: 360,
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      backgroundColor: 'transparent',
      minHeight: 52,
      paddingLeft: '16px',
      paddingRight: '12px',
      fontSize: '0.95rem',
      fontWeight: 500,
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      '& fieldset': {
        borderColor: 'rgba(0,0,0,0.12)',
        borderWidth: 1,
        transition: 'border-color 0.2s, box-shadow 0.2s',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(94, 51, 191, 0.4)',
        boxShadow: '0 0 0 1px rgba(94, 51, 191, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderWidth: 2,
        borderColor: 'var(--color-primary)',
        boxShadow: '0 0 0 3px rgba(94, 51, 191, 0.12)',
      },
    },
    '& .MuiSelect-select': { paddingTop: '14px', paddingBottom: '14px' },
    '& .MuiSelect-icon': { color: 'var(--color-primary)', fontSize: 22 },
  },
};

export default function ServiceTab({
  service,
  timeRangeStart,
  timeRangeEnd,
  onOpenRangePicker,
  refreshInterval,
  onRefreshIntervalChange,
  lastUpdated,
  onRefresh,
  loading,

  health,
  healthLoading,
  healthError,
  onHealthRetry,

  metrics,
  metricsLoading,
  metricsError,
  onMetricsRetry,

  hasEndpointDrilldown = false,
  endpointUri = '',
  onEndpointChange,
  endpointList = [],
  endpointListLoading = false,
  endpointData = null,
  endpointLoading = false,

  hasClientFilter = false,
  availableClients = [],
  clientFilter = 'all',
  onClientFilterChange,

  hasApiAnalytics = false,
}) {
  const hasMetricsBackend = metrics || metricsLoading || metricsError;

  return (
    <Paper sx={styles.serviceBlock} elevation={0}>
      <TabPageHeader
        title={service.name}
        icon={service.icon}
        idPrefix={`monitoring-${service.id}`}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        onOpenRangePicker={onOpenRangePicker}
        refreshInterval={refreshInterval}
        onRefreshIntervalChange={onRefreshIntervalChange}
        lastUpdated={lastUpdated}
        onRefresh={onRefresh}
        loading={loading}
        styles={styles}
      />

      {/* Health */}
      <SectionTitle sx={{ mt: 2 }}>Health</SectionTitle>
      <HealthPanel
        data={health}
        loading={healthLoading}
        error={healthError}
        onRetry={onHealthRetry}
        onRefresh={onHealthRetry}
      />

      {/* HTTP Metrics */}
      <SectionTitle sx={{ mt: 3 }}>HTTP Metrics</SectionTitle>

      {metricsLoading && !metrics && (
        <Box sx={styles.loadingBox}>
          <CircularProgress size={40} sx={{ color: 'var(--color-primary)' }} />
          <Typography variant="body2" color="text.secondary">
            Loading metrics…
          </Typography>
        </Box>
      )}

      {metricsError && !metricsLoading && (
        <Box sx={styles.errorBox}>
          <Typography color="error" gutterBottom>
            {metricsError}
          </Typography>
          <Button
            variant="contained"
            onClick={onMetricsRetry}
            sx={{ bgcolor: 'var(--color-primary)' }}
          >
            Retry
          </Button>
        </Box>
      )}

      {(!metricsLoading || metrics) && !metricsError && metrics && (
        <>
          <Box sx={styles.refreshOverlay}>
            <SummaryCards series={metrics.series} metadata={metrics.metadata} />
            <MetricsGridLayout
              series={metrics.series}
              metadata={metrics.metadata}
              title={`${service.name} - HTTP metrics`}
              layoutStorageKey={`monitoring-${service.id}`}
            />
            {metricsLoading && (
              <Box sx={styles.refreshOverlayInner}>
                <CircularProgress size={40} sx={{ color: 'var(--color-primary)' }} />
              </Box>
            )}
          </Box>

          {/* Endpoint drill-down */}
          {hasEndpointDrilldown && (
            <>
              <SectionTitle sx={{ mt: 3 }}> Per-endpoint drill-down</SectionTitle>
              <Typography variant="body2" sx={styles.endpointSectionDesc}>
                Select an endpoint to view its HTTP metrics (requests, latency).
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <FormControl variant="outlined" sx={styles.endpointSelect}>
                  <Select
                    id="endpoint-uri-select"
                    value={endpointUri}
                    onChange={e => onEndpointChange(e.target.value)}
                    displayEmpty
                    renderValue={v =>
                      v ? v : <span style={{ color: 'var(--t-fg3)' }}>Select endpoint</span>
                    }
                    MenuProps={{
                      PaperProps: { style: { maxHeight: 280 } },
                      MenuListProps: { disablePadding: true },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select endpoint</em>
                    </MenuItem>
                    {endpointListLoading && (
                      <MenuItem value="" disabled>
                        Loading endpoints...
                      </MenuItem>
                    )}
                    {!endpointListLoading &&
                      endpointList.map(uri => (
                        <MenuItem key={uri} value={uri}>
                          {uri}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {hasClientFilter && (
                  <Autocomplete
                    freeSolo
                    openOnFocus
                    id="client-filter-autocomplete"
                    options={['all', ...availableClients]}
                    getOptionLabel={option =>
                      option === 'all' ? 'All clients' : option.toUpperCase()
                    }
                    value={clientFilter}
                    onChange={(_, newValue) => onClientFilterChange(newValue || 'all')}
                    onInputChange={(_, newInput, reason) => {
                      if (reason === 'input' && newInput.trim()) {
                        onClientFilterChange(newInput.trim().toLowerCase());
                      }
                    }}
                    sx={{ minWidth: 200 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Client (e.g. argos)"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '14px',
                            backgroundColor: 'transparent',
                            minHeight: 52,
                            paddingLeft: '16px',
                            fontSize: '0.95rem',
                            fontWeight: 500,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            '& fieldset': {
                              borderColor: 'rgba(0,0,0,0.12)',
                              borderWidth: 1,
                              transition: 'border-color 0.2s, box-shadow 0.2s',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(94, 51, 191, 0.4)',
                              boxShadow: '0 0 0 1px rgba(94, 51, 191, 0.2)',
                            },
                            '&.Mui-focused fieldset': {
                              borderWidth: 2,
                              borderColor: 'var(--color-primary)',
                              boxShadow: '0 0 0 3px rgba(94, 51, 191, 0.12)',
                            },
                          },
                        }}
                      />
                    )}
                  />
                )}
              </Stack>
              {endpointUri && endpointLoading && !endpointData && (
                <Box sx={styles.loadingBox}>
                  <CircularProgress size={36} sx={{ color: 'var(--color-primary)' }} />
                  <Typography variant="body2" color="text.secondary">
                    Loading endpoint metrics…
                  </Typography>
                </Box>
              )}
              {endpointData && (
                <Box sx={{ mt: 3, ...styles.refreshOverlay }}>
                  <MetricsGridLayout
                    series={endpointData.series}
                    metadata={endpointData.metadata}
                    title={`Endpoint: ${endpointData.uri || endpointUri}`}
                    layoutStorageKey="monitoring-endpoint"
                  />
                  {endpointLoading && (
                    <Box sx={styles.refreshOverlayInner}>
                      <CircularProgress size={40} sx={{ color: 'var(--color-primary)' }} />
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </>
      )}

      {/* Coming Soon */}
      {!metricsLoading && !metricsError && !metrics && !hasMetricsBackend && <ComingSoonSection />}

      {!metricsLoading && !metricsError && metrics && !metrics.series?.length && (
        <MetricsCharts series={[]} title={service.name} />
      )}

      {/* API usage analytics */}
      {hasApiAnalytics && (
        <>
          <SectionTitle sx={{ mt: 3 }}>API Usage Analytics</SectionTitle>
          <ApiAnalytics />
        </>
      )}
    </Paper>
  );
}
